import React, { useCallback, useState } from "react";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Box } from "rebass";
import styled from "styled-components";

import { ICompetitionPrize } from "MVVM/Views/Competitions/@types";

const { REACT_APP_STRIPE_PK, REACT_APP_STRIPE_ENDPOINT } = process.env;

const stripePromise = loadStripe(REACT_APP_STRIPE_PK as string);

type LegacyProps = {
  userToken: string;
  returnUrl: string;
  lookup: string;
  additionalLookups?: string[];
  coupon?: string;
  trial?: boolean;
  competitionId?: never;
  prizes?: never;
};

type PrizeCartProps = {
  userToken: string;
  returnUrl: string;
  competitionId: string;
  prizes: ICompetitionPrize[];
  lookup?: never;
  additionalLookups?: never;
  coupon?: never;
  trial?: never;
};

type Props = LegacyProps | PrizeCartProps;

export const StripeCheckout = (props: Props) => {
  const { userToken, returnUrl } = props;
  const [checkoutError, setCheckoutError] = useState(false);
  if (returnUrl) localStorage.setItem("paymentValidationNext", returnUrl);
  // Mark trial checkouts so /payment/validation can tell THIS purchase was a
  // trial (the user's isTrialPeriod alone can be stale when an existing trial
  // user buys a paid plan). Cleared there on consume; cleared here otherwise
  // so an abandoned trial checkout can't taint a later paid one.
  if ("trial" in props && props.trial) {
    localStorage.setItem("paymentValidationTrial", "true");
  } else {
    localStorage.removeItem("paymentValidationTrial");
  }

  const host = window.location.protocol + "//" + window.location.host;

  const fetchClientSecret = useCallback(async () => {
    const body =
      "competitionId" in props && props.competitionId
        ? {
            competitionId: props.competitionId,
            prizes: props.prizes,
            returnUrl: `${host}/payment/validation`,
          }
        : {
            lookup: props.lookup,
            ...(props.additionalLookups?.length
              ? { additionalLookups: props.additionalLookups }
              : {}),
            returnUrl: `${host}/payment/validation`,
            ...(props.trial ? { trial: true } : { coupon: props.coupon }),
          };

    const res = await fetch(`${REACT_APP_STRIPE_ENDPOINT}/stripe/v1/request`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify(body),
    });

    const data = await res.json();
    if (!res.ok || !data.clientSecret) {
      // A rejected session (e.g. the server-side trial eligibility fence)
      // would otherwise hand undefined to EmbeddedCheckoutProvider and hang
      // the modal on a spinner forever.
      setCheckoutError(true);
      throw new Error(data.message || "Failed to create checkout session");
    }
    return data.clientSecret;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  const options = { fetchClientSecret };

  if (checkoutError) {
    return (
      <CheckoutContainer id="checkout">
        <Box py={6} px={5} style={{ textAlign: "center" }}>
          We couldn&apos;t start this checkout. Please refresh and try again, or
          contact support@allstar.gg if it keeps happening.
        </Box>
      </CheckoutContainer>
    );
  }

  return (
    <CheckoutContainer id="checkout">
      <Box py={4}>
        <EmbeddedCheckoutProvider stripe={stripePromise} options={options}>
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </Box>
    </CheckoutContainer>
  );
};

const CheckoutContainer = styled(Box)`
  background-color: ${({ theme }) => theme.colors.navy};
  border-radius: 12px;
  max-width: 1000px;
  overflow: hidden;
  padding: 12;
  width: 90vw;
`;
